import { useEffect, useState } from "react";
import { cx } from "../ui/cx";

/**
 * Sticky banner shown whenever `navigator.onLine === false`. Sits below the
 * header (z-nav) so it never overlaps the main content. Workbox background-sync
 * is still queuing mutations under the hood; this banner reassures the officer.
 */
export function OfflineBanner({ className }: { className?: string }) {
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  if (online) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className={cx(
        "sticky top-0 z-nav flex items-center justify-center gap-2 bg-[var(--c-warning)] px-3 py-1.5 text-xs font-medium text-white shadow-soft",
        className
      )}
    >
      <span aria-hidden>⚠</span>
      <span>You are offline. Your changes will sync automatically when the connection is restored.</span>
      <span className="hidden font-devanagari sm:inline">तुम्ही ऑफलाइन आहात. कनेक्शन परत आल्यावर बदल आपोआप सिंक होतील.</span>
    </div>
  );
}

/**
 * Small chip in the header showing pending background-sync mutation count.
 * Communicates with the service worker via postMessage.
 */
export function SyncQueueChip({ className }: { className?: string }) {
  const [pending, setPending] = useState<number>(0);
  const [syncing, setSyncing] = useState<boolean>(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    function onMessage(ev: MessageEvent) {
      const data = ev.data as { type?: string; pending?: number };
      if (!data) return;
      if (data.type === "sync-queue-status" && typeof data.pending === "number") {
        setPending(data.pending);
      }
      if (data.type === "sync-started") setSyncing(true);
      if (data.type === "sync-finished") setSyncing(false);
    }
    navigator.serviceWorker.addEventListener("message", onMessage);

    // Ask for initial status
    navigator.serviceWorker.ready.then((reg) => {
      reg.active?.postMessage({ type: "request-sync-status" });
    }).catch(() => { /* ignore */ });

    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  function flush() {
    navigator.serviceWorker.ready.then((reg) => reg.active?.postMessage({ type: "flush-sync" }));
  }

  if (pending <= 0 && !syncing) return null;
  return (
    <button
      type="button"
      onClick={flush}
      title="Trigger sync now"
      className={cx(
        "inline-flex items-center gap-1 rounded-full bg-[var(--c-warning-soft)] px-2 py-0.5 text-[11px] font-medium text-[var(--c-warning)] hover:brightness-95 focus-ring",
        className
      )}
    >
      <span aria-hidden className={syncing ? "animate-spin" : ""}>{syncing ? "⟳" : "↑"}</span>
      <span>{syncing ? "Syncing…" : `${pending} pending`}</span>
    </button>
  );
}
